import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { getAll, StateDictionary } from '@appState/helpers';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { EMPTY, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { UnsubscribeData, UserNotificationSub } from './notification-subs.model';
import { UserNotificationSubsService } from './notification-subs.service';

export type NotificationSubsState = {
  isLoading: boolean;
  subs: UserNotificationSub[];
  isUnsubscribeDataLoading: boolean;
  unsubscribeData: UnsubscribeData | null;
};

const initialState: NotificationSubsState = {
  isLoading: false,
  subs: [],
  isUnsubscribeDataLoading: false,
  unsubscribeData: null,
};

const notificationSubsDictionary: StateDictionary<UserNotificationSub> = {
  listLoading: 'isLoading',
  entityList: 'subs',
  createLoading: '',
  selectedEntity: '',
  selectedEntityLoading: '',
  entityId: 'service',
};

export const NotificationSubsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('notification-subs-store'),
  withComputed(state => ({
    hasSubs: computed(() => state.subs().length > 0),
  })),
  withMethods((store, service = inject(UserNotificationSubsService), notificationsService = inject(NotificationsService)) => {
    const repositoryMethods = {
      getAll: getAll<UserNotificationSub, void>({
        store,
        service,
        dictionary: notificationSubsDictionary,
      }),

      updateSubscription: rxMethod<{ subs: UserNotificationSub[] }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ subs }) =>
            service.patchSubscriptions(subs).pipe(
              tap(newSubs => {
                patchState(store, { subs: newSubs, isLoading: false });
                notificationsService.showNotification('Notification settings updated successfully.');
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      getUnsubscribeData: rxMethod<{ data: string }>(
        pipe(
          tap(() => patchState(store, { isUnsubscribeDataLoading: true })),
          switchMap(({ data }) =>
            service.getUnsubscribeData(data).pipe(
              tap(unsubscribeData => patchState(store, { unsubscribeData, isUnsubscribeDataLoading: false })),
              catchError(() => {
                patchState(store, { isUnsubscribeDataLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      unsubscribeFromNotification: rxMethod<{ data: string }>(
        pipe(
          tap(() => patchState(store, { isUnsubscribeDataLoading: true })),
          switchMap(({ data }) =>
            service.unsubscribeFromNotification(data).pipe(
              tap(() => {
                notificationsService.showNotification('Unsubscribed successfully!');
                patchState(store, { isUnsubscribeDataLoading: false });
              }),
              catchError(() => {
                patchState(store, { isUnsubscribeDataLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    return {
      ...repositoryMethods,
    };
  }),
);

export interface NotificationSubsStoreType {
  readonly isLoading: Signal<boolean>;
  readonly subs: Signal<UserNotificationSub[]>;
  readonly isUnsubscribeDataLoading: Signal<boolean>;
  readonly unsubscribeData: Signal<UnsubscribeData | null>;
  readonly hasSubs: Signal<boolean>;

  readonly getAll: () => void;
  readonly updateSubscription: (params: { subs: UserNotificationSub[] }) => void;
  readonly getUnsubscribeData: (params: { data: string }) => void;
  readonly unsubscribeFromNotification: (params: { data: string }) => void;
}
